import React from "react";
import { Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import { Footer } from "../../components/Footer/Footer";
import PhlebotomyHeader from "./components/PhlebotomyHeader";
import { PageContainer } from "./components/PhlebotomySplitPane.styles";
import {
  AddressSupport,
  AddressTitle,
  IntakePageBody,
  IntakePageContent,
} from "./Search/PhlebotomySearchPage.styles";

/**
 * Shown in place of the finder when the phlebotomy search kill switch is off.
 *
 * Patients reach these routes from text messages and clinic emails that were
 * delivered before the switch was flipped, so the destination stays routable
 * and explains itself instead of bouncing them to a login screen.
 */
interface PhlebotomyUnavailablePageProps {
  variant?: "search" | "legacyExpired" | "legacyUnavailable";
}

const COPY_KEYS = {
  legacyExpired: {
    contact: "phlebotomySearch.legacyLinkUnavailable.contact",
    support: "phlebotomySearch.legacyLinkUnavailable.support",
    title: "phlebotomySearch.legacyLinkUnavailable.title",
  },
  legacyUnavailable: {
    contact: "phlebotomySearch.legacyLinkTemporarilyUnavailable.contact",
    support: "phlebotomySearch.legacyLinkTemporarilyUnavailable.support",
    title: "phlebotomySearch.legacyLinkTemporarilyUnavailable.title",
  },
  search: {
    contact: "phlebotomySearch.unavailable.contact",
    support: "phlebotomySearch.unavailable.support",
    title: "phlebotomySearch.unavailable.title",
  },
} as const;

const PhlebotomyUnavailablePage = ({
  variant = "search",
}: PhlebotomyUnavailablePageProps): React.ReactElement => {
  const { t } = useTranslation();
  const copyKeys = COPY_KEYS[variant];

  return (
    <PageContainer>
      <PhlebotomyHeader />
      <IntakePageBody>
        <IntakePageContent role="status" aria-live="polite">
          <AddressTitle component="h1">{t(copyKeys.title)}</AddressTitle>
          <AddressSupport>{t(copyKeys.support)}</AddressSupport>
          <Typography variant="body2" color="text.secondary">
            {t(copyKeys.contact)}
          </Typography>
        </IntakePageContent>
      </IntakePageBody>
      <Footer showLanguageSwitcher requireLanguageFeatureFlag={false} />
    </PageContainer>
  );
};

export default PhlebotomyUnavailablePage;
